import React, { useState, useEffect } from 'react';
import { XMarkIcon, DocumentDuplicateIcon, CheckIcon } from '@heroicons/react/24/outline';
import { useLanguage } from '@/state/language/LanguageProvider';
import api from '@/services/api';

interface PlatformHierarchyTemplate {
  id: string;
  name: string;
  description: string;
  hierarchy_type: 'BUSINESS' | 'FUNCTIONAL' | 'GEOGRAPHIC';
  is_active: boolean;
  created_at: string;
  updated_at: string;
}

interface HierarchyImportModalProps {
  isOpen: boolean;
  onClose: () => void;
  hierarchyType: 'BUSINESS' | 'FUNCTIONAL' | 'GEOGRAPHIC';
  onImport: () => void;
}

export const HierarchyImportModal: React.FC<HierarchyImportModalProps> = ({
  isOpen,
  onClose,
  hierarchyType,
  onImport,
}) => {
  const { t } = useLanguage();
  const [templates, setTemplates] = useState<PlatformHierarchyTemplate[]>([]);
  const [selectedTemplates, setSelectedTemplates] = useState<string[]>([]);
  const [loading, setLoading] = useState(false);
  const [importing, setImporting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (isOpen) {
      loadTemplates();
    }
  }, [isOpen, hierarchyType]);

  const loadTemplates = async () => {
    try {
      setLoading(true);
      setError(null);
      const response = await api.get('/hierarchy-template-management/templates');
      const allTemplates = response.data.data || response.data;
      
      // Filter templates by hierarchy type
      const filteredTemplates = allTemplates.filter(
        (template: PlatformHierarchyTemplate) => template.hierarchy_type === hierarchyType
      );
      
      setTemplates(filteredTemplates);
    } catch (err) {
      setError(t('hierarchy.importModal.loadError'));
      console.error('Error loading templates:', err);
    } finally {
      setLoading(false);
    }
  };

  const handleTemplateToggle = (templateId: string) => {
    setSelectedTemplates(prev => 
      prev.includes(templateId) 
        ? prev.filter(id => id !== templateId)
        : [...prev, templateId]
    );
  };

  const handleImport = async () => {
    if (selectedTemplates.length === 0) {
      setError(t('hierarchy.importModal.selectTemplateRequired'));
      return;
    }

    try {
      setImporting(true);
      setError(null);

      // Import each selected template
      for (const templateId of selectedTemplates) {
        await api.post(`/hierarchy-template-management/templates/${templateId}/import`);
      }

      // Success - close modal and refresh
      onImport();
      onClose();
    } catch (err) {
      setError(t('hierarchy.importModal.importError'));
      console.error('Error importing templates:', err);
    } finally {
      setImporting(false);
    }
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
      <div className="bg-white rounded-lg p-6 w-full max-w-2xl max-h-[80vh] overflow-y-auto">
        <div className="flex justify-between items-center mb-6">
          <h2 className="text-2xl font-bold text-[var(--color-primary-text)]">
{t('hierarchy.importModal.title', { type: hierarchyType })}
          </h2>
          <button
            onClick={onClose}
            className="p-2 hover:bg-gray-100 rounded-full"
          >
            <XMarkIcon className="h-6 w-6" />
          </button>
        </div>

        <p className="text-[var(--color-secondary-text)] mb-6">
{t('hierarchy.importModal.description', { type: hierarchyType.toLowerCase() })}
        </p>

        {error && (
          <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded mb-4">
            {error}
          </div>
        )}

        {loading ? (
          <div className="flex items-center justify-center py-8">
            <div className="text-[var(--color-surface-text)]">{t('hierarchy.importModal.loading')}</div>
          </div>
        ) : (
          <div className="space-y-4 mb-6">
            {templates.length === 0 ? (
              <div className="text-center py-8 text-[var(--color-secondary-text)]">
                No {hierarchyType.toLowerCase()} templates available
              </div>
            ) : (
              templates.map((template) => (
                <div
                  key={template.id}
                  className={`border rounded-lg p-4 cursor-pointer transition-colors ${
                    selectedTemplates.includes(template.id)
                      ? 'border-blue-500 bg-blue-50'
                      : 'border-gray-200 hover:border-gray-300'
                  }`}
                  onClick={() => handleTemplateToggle(template.id)}
                >
                  <div className="flex items-start space-x-3">
                    <div className="flex-shrink-0">
                      {selectedTemplates.includes(template.id) ? (
                        <div className="w-5 h-5 bg-blue-500 rounded-full flex items-center justify-center">
                          <CheckIcon className="h-3 w-3 text-white" />
                        </div>
                      ) : (
                        <div className="w-5 h-5 border-2 border-gray-300 rounded-full"></div>
                      )}
                    </div>
                    <div className="flex-1">
                      <h3 className="font-semibold text-[var(--color-surface-text)] mb-1">
                        {template.name}
                      </h3>
                      <p className="text-sm text-[var(--color-secondary-text)]">
                        {template.description}
                      </p>
                    </div>
                    <DocumentDuplicateIcon className="h-5 w-5 text-gray-400" />
                  </div>
                </div>
              ))
            )}
          </div>
        )}

        <div className="flex justify-end space-x-3">
          <button
            onClick={onClose}
            className="px-4 py-2 text-gray-600 hover:text-gray-800 transition-colors"
            disabled={importing}
          >
            Cancel
          </button>
          <button
            onClick={handleImport}
            disabled={selectedTemplates.length === 0 || importing}
            className="px-6 py-2 bg-[var(--color-primary-bg)] text-[var(--color-primary-text)] rounded-md hover:opacity-90 transition-opacity disabled:opacity-50 disabled:cursor-not-allowed"
          >
{importing ? t('hierarchy.importModal.importing') : t('hierarchy.importModal.importButton', { count: selectedTemplates.length })}
          </button>
        </div>
      </div>
    </div>
  );
};
